var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/init-module.ts
import { setDefaultAssets } from "@leagueplatform/asset-config";

// src/asset-map.ts
import error from "./assets/error.svg";
import closeIcon from "./assets/close-icon.svg";
var QUESTIONNAIRE_FLOW_ASSETS_MAP = {
  QUESTIONNAIRE_FLOW_ERROR: error,
  QUESTIONNAIRE_FLOW_CLOSE_ICON: closeIcon
};

// src/init-module.ts
var initModule = () => {
  setDefaultAssets(QUESTIONNAIRE_FLOW_ASSETS_MAP);
};

// src/pages/questionnaire-flow.page.tsx
import React2, { useEffect, useRef, useState as useState2 } from "react";
import {
  Box as Box2,
  Button,
  HeadingText,
  Spinner,
  styled
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";

// src/components/slide/slide.component.tsx
import React, { useMemo } from "react";
import { Box, Flex, css } from "@leagueplatform/genesis-commons";
import { usePrefersReducedMotion } from "@leagueplatform/web-common";
var EASING = Object.freeze({
  IN_OUT_QUAD: `cubic-bezier(0.45, 0, 0.55, 1)`
});
var baseDurationMs = 150;
var TRANSITION_DURATION_MS = Object.freeze({
  SHORT: baseDurationMs,
  MEDIUM: baseDurationMs * 2,
  LONG: baseDurationMs * 3,
  XLONG: baseDurationMs * 4
});
var { MEDIUM, XLONG } = TRANSITION_DURATION_MS;
var { IN_OUT_QUAD } = EASING;
var TRANSITIONS = Object.freeze({
  PAGE: `${XLONG}ms ${IN_OUT_QUAD}`,
  HALF_PAGE: `${MEDIUM}ms ${IN_OUT_QUAD}`
});
var slideTransition = `transform ${TRANSITIONS.PAGE}, opacity ${TRANSITIONS.PAGE}`;
var reducedMotionSlideTransition = `opacity ${TRANSITIONS.PAGE}`;
var reducedMotionSlideStyles = css({
  left: 0,
  transform: "none",
  transition: reducedMotionSlideTransition
});
var useSlideTransitionStyles = ({
  slideIndex,
  gap,
  activeIndex
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const slideStyles = css({
    left: `calc(${slideIndex} * ${gap})`,
    transform: `translateX(calc(-${gap} * ${activeIndex}))`,
    transition: slideTransition
  });
  return useMemo(
    () => prefersReducedMotion ? reducedMotionSlideStyles : slideStyles,
    [prefersReducedMotion, slideStyles]
  );
};
var Slide = ({
  children,
  gap = "100%",
  slideIndex,
  activeIndex
}) => {
  const slideTransitionStyles = useSlideTransitionStyles({
    gap,
    slideIndex,
    activeIndex
  });
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      "aria-hidden": activeIndex !== slideIndex,
      css: slideTransitionStyles,
      height: "100%",
      opacity: Number(activeIndex === slideIndex),
      position: "absolute",
      top: 0,
      display: "flex",
      width: "100%"
    },
    children
  );
};

// src/pages/questionnaire-flow.page.tsx
import { DynamicFHIRForm } from "@leagueplatform/fhir-questionnaire";

// src/hooks/post-generate-questionnaire.ts
import { leagueFetch } from "@leagueplatform/league-fetch";

// src/constants/questionnaire.constants.ts
var GENERATE_QUESTIONNAIRE_PATH = "/v1/generate-questionnaire";
var NEXT_QUESTIONNAIRE_PATH = "/v1/next";

// src/hooks/post-generate-questionnaire.ts
var postGenerateQuestionnaire = (_0) => __async(void 0, [_0], function* ({
  questionnaireId,
  answers = []
}) {
  const response = yield leagueFetch(GENERATE_QUESTIONNAIRE_PATH, {
    method: "POST",
    body: JSON.stringify({
      data: {
        type: "questionnaire",
        attributes: {
          nodeId: questionnaireId,
          answers
        }
      }
    })
  });
  if (response.ok) {
    return response.json();
  }
  throw new Error(`postGenerateQuestionnaire error: ${response.status}`);
});

// src/hooks/use-get-questionnaire.hook.ts
import { useMutation } from "react-query";
var useGetQuestionnaire = () => useMutation(
  ({ questionnaireId, answers }) => postGenerateQuestionnaire({ questionnaireId, answers })
);

// src/hooks/use-post-questionnaire-next-node.hook.ts
import { useMutation as useMutation2 } from "react-query";

// src/hooks/post-questionnaire-next-node.ts
import { leagueFetch as leagueFetch2 } from "@leagueplatform/league-fetch";
var postQuestionnaireNextNode = (_0) => __async(void 0, [_0], function* ({
  nodeId,
  answers
}) {
  const response = yield leagueFetch2(`${NEXT_QUESTIONNAIRE_PATH}`, {
    method: "POST",
    body: JSON.stringify({
      data: {
        type: "next-request",
        attributes: {
          nodeId,
          answers
        }
      }
    })
  });
  if (!response.ok) {
    throw new Error(`postQuestionnaireNextNode error: ${response.status}`);
  }
  return response.json();
});

// src/hooks/use-post-questionnaire-next-node.hook.ts
var usePostQuestionnaireNextNode = () => useMutation2(
  ({
    nodeId,
    answers
  }) => postQuestionnaireNextNode({ nodeId, answers })
);

// src/pages/questionnaire-flow.page.tsx
import { ErrorState } from "@leagueplatform/web-common-components";
import { handleStaticAsset } from "@leagueplatform/asset-config";

// src/hooks/use-should-hide-attachment.hook.ts
import { useState } from "react";
var useShouldHideAttachment = () => {
  const topicKey = "code";
  const topicValuePrefix = "MGC-CHN";
  const questionnaireKey = "linkId";
  const questionnaireValue = "topics-list";
  const [hideAttachment, setHideAttachment] = useState(false);
  const findNestedKeyWithPrefix = (obj, keyToFind, prefixToFind, validQuestionnaireKey, validQuestionnaireValue) => {
    const result = {
      isTopicQuestionnaire: false,
      status: false
    };
    const helper = (nestedObj) => {
      if (nestedObj === null || typeof nestedObj !== "object") {
        return;
      }
      if (Object.prototype.hasOwnProperty.call(nestedObj, "linkId") && nestedObj[validQuestionnaireKey] === validQuestionnaireValue) {
        result.isTopicQuestionnaire = true;
      }
      if (result.isTopicQuestionnaire && Object.prototype.hasOwnProperty.call(nestedObj, keyToFind) && nestedObj[keyToFind].startsWith(prefixToFind)) {
        result.status = true;
      }
      Object.keys(nestedObj).forEach((key) => {
        if (!result.status) {
          helper(nestedObj[key]);
        }
      });
    };
    helper(obj);
    return result;
  };
  const shouldHideAttachment = (answer) => {
    const { isTopicQuestionnaire, status } = findNestedKeyWithPrefix(
      answer,
      topicKey,
      topicValuePrefix,
      questionnaireKey,
      questionnaireValue
    );
    if (isTopicQuestionnaire && status)
      setHideAttachment(true);
    return { isTopicQuestionnaire, status };
  };
  return {
    hideAttachment,
    shouldHideAttachment
  };
};

// src/pages/questionnaire-flow.page.tsx
var Wrapper = styled(Box2, {
  backgroundColor: "$surfaceBackgroundTertiary",
  display: "flex",
  flexDirection: "column",
  position: "absolute",
  inset: 0,
  overflowX: "hidden"
});
var Body = styled(Box2, {
  flex: 1,
  paddingTop: "$threeQuarters"
});
var Footer = styled(Box2, {
  display: "flex",
  flexGrow: 0,
  justifyContent: "center",
  paddingTop: "$oneAndHalf",
  paddingBottom: "$oneAndHalf"
});
var Slider = styled(Box2, {
  display: "flex",
  position: "relative",
  flexGrow: 1,
  height: "100%",
  width: "100%",
  maxWidth: 632,
  margin: "0 auto"
});
var SlideWrap = styled(Box2, {
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  backgroundColor: "$surfaceBackgroundPrimary",
  padding: "$two",
  borderRadius: "$large",
  overflowY: "auto"
});
var ErrorWrapper = styled(SlideWrap, {
  justifyContent: "center",
  position: "absolute",
  inset: 0,
  zIndex: 9
});
var LoadingWrapper = styled(Box2, {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%"
});
var QuestionnaireFlow = ({
  title,
  firstQuestionnaireId,
  onClose,
  onCompleted
}) => {
  const { formatMessage } = useIntl();
  const [pages, setPages] = useState2([]);
  const [isTransitioning, setIsTransitioning] = useState2(false);
  const [currentPage, setCurrentPage] = useState2(0);
  const [currentQuestionnaireId, setCurrentQuestionnaireId] = useState2("");
  const [questionnaireAnswers, setQuestionnaireAnswers] = useState2([]);
  const [lastRequest, setLastRequest] = useState2(null);
  const formRefs = useRef([]);
  formRefs.current = [];
  const { hideAttachment, shouldHideAttachment } = useShouldHideAttachment();
  useEffect(() => {
    if (pages.length > 0) {
      setCurrentPage(pages.length - 1);
    }
  }, [pages]);
  const generateQuestionnaireMutation = useGetQuestionnaire();
  const postNextNodeMutation = usePostQuestionnaireNextNode();
  const nextStep = (data) => {
    setPages((current) => [...current, data]);
  };
  const setQuestionnaireData = (data, variables) => {
    setCurrentQuestionnaireId(variables.questionnaireId);
    nextStep(data);
  };
  const getQuestionnaireData = ({
    questionnaireId,
    answers
  }) => {
    setLastRequest({
      fn: getQuestionnaireData,
      data: {
        questionnaireId,
        answers
      }
    });
    generateQuestionnaireMutation.mutate(
      {
        questionnaireId,
        answers: answers ? [answers] : []
      },
      {
        onSuccess: (data, variables) => {
          setLastRequest(null);
          setQuestionnaireData(data, variables);
        }
      }
    );
  };
  useEffect(() => {
    if (firstQuestionnaireId) {
      getQuestionnaireData({
        questionnaireId: firstQuestionnaireId
      });
    }
  }, [firstQuestionnaireId]);
  const nextQuestionnaire = (answers) => {
    shouldHideAttachment(answers);
    setLastRequest({
      fn: nextQuestionnaire,
      data: answers
    });
    setQuestionnaireAnswers((current) => [...current, answers]);
    postNextNodeMutation.mutate(
      {
        nodeId: currentQuestionnaireId,
        answers: answers ? [answers] : []
      },
      {
        onSuccess({ data }) {
          var _a, _b;
          setLastRequest(null);
          if ((_a = data == null ? void 0 : data.attributes) == null ? void 0 : _a.completed) {
            const newDraftThread = {
              subject: formatMessage({ id: "QUESTIONNAIRE_FLOW_NEW_MESSAGE" }),
              participants: [],
              answers: [...questionnaireAnswers, answers]
              // Include the new answers
            };
            onCompleted(newDraftThread, hideAttachment);
            return;
          }
          getQuestionnaireData({
            questionnaireId: (_b = data == null ? void 0 : data.attributes) == null ? void 0 : _b.nodeId,
            answers
          });
        }
      }
    );
  };
  const addToRefs = (el) => {
    var _a;
    if (el && !((_a = formRefs.current) == null ? void 0 : _a.includes(el))) {
      formRefs.current.push(el);
    }
  };
  const prevStep = () => {
    var _a;
    if (!isTransitioning) {
      const previousPage = pages[currentPage - 1];
      setIsTransitioning(true);
      setCurrentQuestionnaireId((_a = previousPage == null ? void 0 : previousPage.data) == null ? void 0 : _a.id);
      setTimeout(() => {
        setPages(pages.slice(0, -1));
        setIsTransitioning(false);
      }, 500);
    }
  };
  const triggerFormSubmit = () => {
    if (formRefs.current[currentPage]) {
      const event = new MouseEvent("click", {
        bubbles: true,
        cancelable: true,
        view: window
      });
      formRefs.current[currentPage].dispatchEvent(event);
    }
  };
  const isLoading = generateQuestionnaireMutation.isLoading || postNextNodeMutation.isLoading;
  const isError = generateQuestionnaireMutation.isError || postNextNodeMutation.isError;
  const errorImage = handleStaticAsset(
    "QUESTIONNAIRE_FLOW_ERROR" /* QUESTIONNAIRE_FLOW_ERROR */
  );
  const retryRequest = () => {
    lastRequest == null ? void 0 : lastRequest.fn(lastRequest == null ? void 0 : lastRequest.data);
  };
  const closeQuestionnaire = () => {
    var _a, _b, _c, _d;
    return onClose((_d = (_c = (_b = (_a = pages[currentPage]) == null ? void 0 : _a.data) == null ? void 0 : _b.attributes) == null ? void 0 : _c.response) == null ? void 0 : _d.title);
  };
  return /* @__PURE__ */ React2.createElement(Wrapper, null, /* @__PURE__ */ React2.createElement(Body, null, /* @__PURE__ */ React2.createElement(
    Button,
    {
      onClick: closeQuestionnaire,
      priority: "secondary",
      icon: "tinyClose",
      hideLabel: true,
      quiet: true,
      size: "small",
      css: {
        position: "absolute",
        top: "$threeQuarters",
        right: 40,
        zIndex: 1
      }
    },
    formatMessage({ id: "QUESTIONNAIRE_FLOW_CLOSE" })
  ), /* @__PURE__ */ React2.createElement(Slider, null, isError && /* @__PURE__ */ React2.createElement(ErrorWrapper, null, /* @__PURE__ */ React2.createElement(ErrorState, { hideButton: true, imageSrc: errorImage })), isLoading && !pages.length && /* @__PURE__ */ React2.createElement(LoadingWrapper, null, /* @__PURE__ */ React2.createElement(Spinner, { loading: true })), pages.map((page, index) => {
    var _a, _b, _c, _d;
    return /* @__PURE__ */ React2.createElement(
      Slide,
      {
        key: (_a = page.data) == null ? void 0 : _a.id,
        activeIndex: currentPage,
        gap: "120%",
        slideIndex: index
      },
      /* @__PURE__ */ React2.createElement(SlideWrap, null, /* @__PURE__ */ React2.createElement(HeadingText, { level: "1", size: "xl" }, title), /* @__PURE__ */ React2.createElement(
        DynamicFHIRForm,
        {
          FHIRQuestionnaire: (_d = (_c = (_b = pages[index]) == null ? void 0 : _b.data) == null ? void 0 : _c.attributes) == null ? void 0 : _d.response,
          setQuestionnaireAnswer: nextQuestionnaire,
          handleSubmitInParent: true,
          ref: addToRefs
        }
      ))
    );
  }))), /* @__PURE__ */ React2.createElement(Footer, null, pages.length > 1 && /* @__PURE__ */ React2.createElement(
    Button,
    {
      onClick: prevStep,
      quiet: true,
      priority: "secondary",
      css: {
        marginRight: "$one"
      }
    },
    formatMessage({ id: "QUESTIONNAIRE_FLOW_GO_BACK" })
  ), !isError && /* @__PURE__ */ React2.createElement(Button, { onClick: triggerFormSubmit, loading: isLoading }, formatMessage({ id: "QUESTIONNAIRE_FLOW_CONTINUE" })), isError && /* @__PURE__ */ React2.createElement(Button, { onClick: retryRequest }, formatMessage({ id: "QUESTIONNAIRE_FLOW_RETRY" }))));
};

// src/index.ts
initModule();
export {
  QuestionnaireFlow
};
//# sourceMappingURL=index.js.map